/**
 * Cross-tab refresh coordination for the FusionAuth React SDK.
 *
 * Problem: with 60-second JWTs + OneTimeUse refresh tokens, every browser tab
 * runs its own SDK timer and tries to refresh at roughly the same moment.
 * All but one lose the race against the rotated refresh token and receive a
 * 400 / missing_refresh_token. The SDK's onAutoRefreshFailure then logs the
 * loser out, and (separately) the /app/logout endpoint clears the shared
 * parent-domain cookies, cascading the logout to every tab.
 *
 * This coordinator wraps `window.fetch` and intercepts any POST to a URL
 * matching `/app/refresh/?`. Calls are serialized across tabs via the Web
 * Locks API (`navigator.locks`), which IS atomic across same-origin browsing
 * contexts. Inside the lock, the coordinator checks the `app.at_exp` cookie
 * — if a prior lock holder already refreshed the session, the call is
 * answered with a synthetic 200 instead of hitting FA again. This both
 * eliminates the race and avoids redundant refresh traffic when many tabs
 * are open.
 *
 * Pair with the `onAutoRefreshFailure` recovery in `main.tsx` which handles
 * the edge case where a tab still ends up needing to recover from a stale
 * SDK timer.
 */

const REFRESH_LOCK = 'em3-fa-refresh';
const REFRESH_URL_PATTERN = /\/app\/refresh\/?(\?|$)/;
// If app.at_exp still has more than this remaining, another tab refreshed
// recently enough that this tab can safely skip the network call.
const FRESH_THRESHOLD_MS = 30_000;

function getAtExpMs(): number {
  const raw = document.cookie
    .split('; ')
    .find((c) => c.startsWith('app.at_exp='))
    ?.split('=')[1];
  return raw ? parseInt(raw, 10) * 1000 : 0;
}

function getUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  if (input instanceof Request) return input.url;
  return String(input);
}

let installed = false;

export function installRefreshCoordinator(): void {
  if (installed) return;
  installed = true;

  if (typeof navigator === 'undefined' || typeof navigator.locks === 'undefined') {
    console.warn('[refreshCoordinator] Web Locks API unavailable; cross-tab serialization disabled.');
    return;
  }

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = getUrl(input);
    if (!REFRESH_URL_PATTERN.test(url)) {
      return originalFetch(input, init);
    }

    return navigator.locks.request(REFRESH_LOCK, { mode: 'exclusive' }, async () => {
      const atExpMs = getAtExpMs();
      const remaining = atExpMs - Date.now();
      if (remaining > FRESH_THRESHOLD_MS) {
        // Another tab refreshed recently enough that the SDK doesn't need to
        // hit FA again. Return a synthetic 200 — the SDK only checks
        // response.ok and then calls scheduleTokenExpiration(), which reads
        // app.at_exp from the cookie jar.
        console.log(
          `[refreshCoordinator] skipping refresh — app.at_exp has ${Math.round(remaining / 1000)}s remaining`,
        );
        return new Response('{}', {
          status: 200,
          headers: { 'Content-Type': 'application/json' },
        });
      }
      console.log('[refreshCoordinator] running actual /app/refresh/ call');
      return originalFetch(input, init);
    });
  };
}
